import { useCallback } from "react"
import { useLocation, useSearchParams } from "react-router"

import {
  stageSearchParamUpdates,
  useSearchParamStagingScope,
} from "@/utils/url/url-utils"

interface UseSearchParamsYearValue {
  isYearInSearchParams: boolean
  searchParamsYear: number | undefined
  setSearchParamsYear: (
    year: number,
    options?: SetSearchParamsYearOptions
  ) => void
}

interface SetSearchParamsYearOptions {
  replace?: boolean
}

/**
 * Manages the year value in the search params.
 *
 * @example
 * const {
 *   isYearInSearchParams,
 *   searchParamsYear,
 *   setSearchParamsYear
 * } = useSearchParamsYear()
 */
export const useSearchParamsYear = (): UseSearchParamsYearValue => {
  const location = useLocation()
  const searchParamStagingScope = useSearchParamStagingScope()
  const [searchParams, setSearchParams] = useSearchParams()

  const searchParamsYear: number | undefined =
    Number.parseInt(searchParams.get("year") ?? "") || undefined

  const isYearInSearchParams = Boolean(searchParamsYear)

  const setSearchParamsYear = useCallback(
    (year: number, options: SetSearchParamsYearOptions = {}) => {
      const { replace = false } = options

      stageSearchParamUpdates({
        committed: searchParams,
        locationKey: location.key,
        replace,
        scope: searchParamStagingScope,
        setSearchParams,
        updates: { year },
      })
    },
    [location.key, searchParamStagingScope, searchParams, setSearchParams]
  )

  return {
    isYearInSearchParams,
    searchParamsYear,
    setSearchParamsYear,
  }
}
